import React, { useCallback, useEffect, useState } from 'react';
import { readContract, writeContract, waitForTransactionReceipt } from 'wagmi/actions';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { zeroAddress, zeroHash } from 'viem';
import toast from 'react-hot-toast';

import NavHeader from '../components/NavHeader.js';
import FooterSection from '../components/FooterSection.js';
import { GlassCard, ConnectWalletPrompt } from '../components/shared/GlassCard.js';
import { GradientText } from '../components/shared/GradientText.js';
import ReferralStatsGrid from '../components/referral/ReferralStatsGrid.js';
import ReferralTreeSection from '../components/referral/ReferralTreeSection.js';
import { config } from '../config.jsx';
import vaultAbi from '../config/vaultAbi.json';
import { coreVaultAddress, refTopAddr, refTopUser } from '../utils/addressHelpers.ts';
import { BASE_URL, BSC_CHAIN_ID } from '../utils/constants.ts';
import { getStoredReferralCode } from '../components/RefLinkCapture.js';
import {
  REFERRAL_REGISTER_TEXT_MAX,
  decodeReferralBytes32FromContract,
  getRefTextFromCookie,
  referralTextToBytes32,
} from '../utils/referralHelpers.js';

function Referral() {
  const { address, chainId } = useAccount();
  const { open } = useAppKit();
  const [loading, setLoading] = useState(false);
  const [txPending, setTxPending] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [referralAllIncome, setReferralAllIncome] = useState(0);
  const [partnerLengths, setPartnerLengths] = useState([]);
  const [referralUpline, setReferralUpline] = useState('');
  const [referralUplineUser, setReferralUplineUser] = useState('');
  const [refAddrFromText, setRefAddrFromText] = useState('');
  const [userReferralLinkText, setUserReferralLinkText] = useState('');
  const [hasContractReferral, setHasContractReferral] = useState(false);
  const [referralRegisterInput, setReferralRegisterInput] = useState('');

  const loadReferralData = useCallback(async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      setReferralAllIncome(0);
      setPartnerLengths([]);
      setReferralUpline('');
      setReferralUplineUser('');
      setRefAddrFromText('');
      setUserReferralLinkText('');
      setHasContractReferral(false);
      return;
    }
    setLoading(true);
    try {
      const cookieRef = getStoredReferralCode();
      const refStr =
        cookieRef && typeof cookieRef === 'string' && cookieRef.length >= 4 ? cookieRef : '';
      const refBytes32 = referralTextToBytes32(refStr);
      const userDatas = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [address, refBytes32],
        chainId: BSC_CHAIN_ID,
      });

      const rawUpline = userDatas[1][0];
      setRefAddrFromText(userDatas[1][1]);
      setReferralUpline(
        rawUpline && rawUpline !== '0x0000000000000000000000000000000000000000' ? rawUpline : refTopAddr
      );

      const uplineDatas = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [rawUpline, refBytes32],
        chainId: BSC_CHAIN_ID,
      });
      const rawUplineUser = decodeReferralBytes32FromContract(uplineDatas[4]);
      setReferralUplineUser(
        rawUplineUser && rawUpline !== '0x0000000000000000000000000000000000000000'
          ? rawUplineUser
          : refTopUser
      );
      setReferralAllIncome(Number(userDatas[3]) / 10 ** 18);
      const lengths = userDatas[2];
      setPartnerLengths(Array.isArray(lengths) ? [...lengths] : []);

      const linkRaw = userDatas[4];
      const linkHex =
        typeof linkRaw === 'string' && linkRaw.startsWith('0x') ? linkRaw.toLowerCase() : '';
      const onChain = Boolean(linkHex) && linkHex !== zeroHash.toLowerCase();
      setHasContractReferral(onChain);
      setUserReferralLinkText(onChain ? decodeReferralBytes32FromContract(linkRaw) : '');

    } catch (e) {
      console.error(e);
      toast.error('Could not load affiliate data');
      setReferralAllIncome(0);
      setPartnerLengths([]);
      setReferralUpline('');
      setReferralUplineUser('');
      setRefAddrFromText('');
      setUserReferralLinkText('');
      setHasContractReferral(false);
    } finally {
      setLoading(false);
    }
  }, [address, chainId]);

  useEffect(() => {
    loadReferralData();
  }, [loadReferralData]);

  useEffect(() => {
    const ref = getStoredReferralCode();
    if (ref && ref.length >= 4 && referralUpline === '' && refAddrFromText) {
      setReferralUpline(
        refAddrFromText !== '0x0000000000000000000000000000000000000000' ? refAddrFromText : refTopAddr
      );
    }
    if (ref && ref.length >= 4 && referralUpline === '') {
      setReferralUplineUser(
        refAddrFromText !== '0x0000000000000000000000000000000000000000' ? ref : refTopUser
      );
    }
  }, [refAddrFromText, referralUpline]);

  const handleReferralUplineCopy = () => {
    const toCopy = referralUpline || refTopAddr;
    if (toCopy) {
      navigator.clipboard.writeText(toCopy);
      toast.success('Address copied');
    }
  };

  const uplineDisplayUser = referralUplineUser || refTopUser || '';
  const referralAlreadyRegistered = hasContractReferral;
  const myReferralFullLink =
    hasContractReferral && userReferralLinkText
      ? `${BASE_URL}/ref?=${encodeURIComponent(userReferralLinkText)}`
      : '';
  const referralRegisterDisabled =
    txPending || referralAlreadyRegistered || referralRegisterInput.length < 4;

  const handleCopyMyReferralLink = () => {
    if (!myReferralFullLink) return;
    navigator.clipboard.writeText(myReferralFullLink);
    toast.success('Affiliate link copied');
  };

  const openRegistration = () => {
    setReferralRegisterInput('');
    setRegistrationOpen(true);
  };

  const closeRegistration = () => {
    if (txPending) return;
    setRegistrationOpen(false);
    setReferralRegisterInput('');
  };

  const runReferralRegister = async () => {
    if (!address || chainId !== BSC_CHAIN_ID) {
      toast.error('Connect on BNB Smart Chain');
      return;
    }
    if (hasContractReferral) {
      toast.error('You already have an affiliate code on file');
      return;
    }
    const referralInput = referralRegisterInput.trim();
    if (referralInput.length < 4) {
      toast.error('Affiliate code must be at least 4 characters');
      return;
    }
    if (referralInput.length > REFERRAL_REGISTER_TEXT_MAX) {
      toast.error(`Affiliate code must be at most ${REFERRAL_REGISTER_TEXT_MAX} characters`);
      return;
    }

    try {
      setTxPending(true);
      const refText = getRefTextFromCookie();
      const refBytes32 = referralTextToBytes32(refText);
      const referralBytes32 = referralTextToBytes32(referralInput);

      const getUserRefData = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [address, refBytes32],
        chainId: BSC_CHAIN_ID,
      });
      const getUserRefText = await readContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'getUserDatas',
        args: [address, referralBytes32],
        chainId: BSC_CHAIN_ID,
      });

      const refOwner = getUserRefText[1][1];
      if (
        refOwner?.toLowerCase() !== address.toLowerCase() &&
        refOwner?.toLowerCase() !== zeroAddress.toLowerCase()
      ) {
        toast.error('This affiliate link has already been used');
        return;
      }

      const refAddress = getUserRefData[1][1];
      let referralAddress = getUserRefData[1][0];
      const zero = zeroAddress.toLowerCase();

      if (!referralAddress || referralAddress.toLowerCase() === zero) {
        if (!refAddress || refAddress.toLowerCase() === zero) {
          toast.error('An affiliate link is required to register');
          return;
        }
        referralAddress = refAddress;
      }

      const regHash = await writeContract(config, {
        address: coreVaultAddress,
        abi: vaultAbi,
        functionName: 'register',
        args: [referralAddress, referralBytes32],
        chainId: BSC_CHAIN_ID,
      });
      await waitForTransactionReceipt(config, { hash: regHash });
      toast.success('Registration complete');
      closeRegistration();
      await loadReferralData();
    } catch (e) {
      console.error(e);
      toast.error(e?.shortMessage || e?.message || 'Registration failed');
    } finally {
      setTxPending(false);
    }
  };

  if (!address) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark">
        <NavHeader />
        <main className="pt-nav pb-24 px-4 flex justify-center">
          <ConnectWalletPrompt message="Connect your wallet to view affiliate stats and register your code" />
        </main>
        <FooterSection />
      </div>
    );
  }

  if (chainId !== BSC_CHAIN_ID) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark py-32">
        <NavHeader />
        <main className="pt-nav pb-36 px-4 flex justify-center">
          <GlassCard variant="gold" className="p-8 max-w-md text-center">
            <p className="text-gray-300 mb-4">Affiliates are available on BNB Smart Chain (BSC).</p>
            <button type="button" className="btn-gold px-6 py-2 text-sm" onClick={() => open()}>
              Switch network in wallet
            </button>
          </GlassCard>
        </main>
        <FooterSection />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark via-dark-secondary to-dark relative">
      {loading && (
        <>
          <style>
            {`
              @keyframes hmReferralBarSlide {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(400%); }
              }
              .hm-referral-bar-fill {
                width: 40%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(238, 212, 91, 0.95), transparent);
                animation: hmReferralBarSlide 1.1s ease-in-out infinite;
              }
            `}
          </style>
          <div
            className="fixed left-0 right-0 top-20 z-[60] flex flex-col border-b border-hash-gold/10 shadow-[0_4px_24px_rgba(0,0,0,0.35)]"
            aria-busy="true"
          >
            <div
              className="h-1 w-full overflow-hidden bg-hash-gold/15"
              role="progressbar"
              aria-valuetext="Loading affiliates"
            >
              <div className="hm-referral-bar-fill" />
            </div>
            <div
              className="flex items-center justify-center gap-2 border-b border-hash-gold/15 bg-dark/95 py-2 px-3 backdrop-blur-md"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-hash-gold shadow-[0_0_8px_rgba(238,212,91,0.6)] motion-safe:animate-pulse"
                aria-hidden
              />
              <span className="text-center text-[11px] font-medium tracking-wide text-hash-gold/95 sm:text-xs">
                Loading affiliate stats and team data…
              </span>
            </div>
          </div>
        </>
      )}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: 'url(/images/background-games.jpg)',
          opacity: 0.12,
        }}
      />
      <NavHeader />

      <main
        className={`pb-16 px-4 sm:px-6 lg:px-8 relative z-10 ${loading ? 'pt-nav-loading' : 'pt-nav'}`}
      >
        <div className="container-premium max-w-5xl mx-auto">
          <h1 className="page-title text-3xl sm:text-4xl font-bold text-center">
            <GradientText variant="medora" className="text-3xl sm:text-4xl font-bold">
              Affiliates
            </GradientText>
          </h1>
          <p className="page-subtitle text-gray-400 text-center text-sm">
            Affiliate income, direct partners, and upline from the factory contract
          </p>

          <ReferralStatsGrid
            allIncome={referralAllIncome}
            partnerLengths={partnerLengths}
            uplineAddress={referralUpline}
            uplineUser={referralUplineUser}
            refTopAddr={refTopAddr}
            refTopUser={refTopUser}
            onCopyAddress={handleReferralUplineCopy}
            referralFullLink={myReferralFullLink}
            onCopyReferralLink={handleCopyMyReferralLink}
            loading={loading}
          />

          <div className="flex justify-center mt-6 mb-10">
            <button
              type="button"
              className="btn-gold px-8 py-3 text-sm font-semibold rounded-lg"
              onClick={openRegistration}
              disabled={loading}
            >
              {referralAlreadyRegistered ? 'Manage affiliate code' : 'Register affiliate code'}
            </button>
          </div>

          <ReferralTreeSection partnerLengths={partnerLengths} />
        </div>
      </main>

      {registrationOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && closeRegistration()}
        >
          <GlassCard variant="premium" className="p-6 w-full max-w-md relative z-[101]">
            <h2 className="text-lg font-bold text-white mb-4">
              {referralAlreadyRegistered ? 'Affiliates' : 'Affiliate registration'}
            </h2>

            <label className="text-gray-400 text-sm mb-2 block">Upline user ID</label>
            <div className="w-full rounded-lg bg-dark-secondary/80 border border-hash-gold/20 px-4 py-3 text-hash-gold font-semibold text-sm break-all mb-1">
              {uplineDisplayUser || '—'}
            </div>
            <p className="text-gray-500 text-xs mb-4">Read-only (from your affiliate data)</p>

            {referralAlreadyRegistered ? (
              <>
                {myReferralFullLink ? (
                  <>
                    <label className="text-gray-400 text-sm mb-2 block">Your affiliate link</label>
                    <div className="flex flex-col sm:flex-row gap-2 items-stretch mb-2">
                      <div className="flex-1 rounded-lg bg-dark-secondary border border-hash-gold/20 px-4 py-3 text-white text-sm break-all min-w-0">
                        {myReferralFullLink}
                      </div>
                      <button
                        type="button"
                        className="btn-gold px-5 py-2.5 text-sm shrink-0 self-center sm:self-auto"
                        onClick={handleCopyMyReferralLink}
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-emerald-400/90 text-sm mb-4">Share this link to invite others.</p>
                  </>
                ) : (
                  <p className="text-emerald-400/90 text-sm mb-4">
                    You already have an affiliate code on file.
                  </p>
                )}
                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    className="btn-gold px-5 py-2 text-sm"
                    onClick={closeRegistration}
                    disabled={txPending}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className="text-gray-400 text-sm mb-2 block" htmlFor="affiliate-register-text">
                  Prefered User ID
                  <span className="text-gray-600 font-normal">
                    {' '}
                    (max 20 characters)
                  </span>
                </label>
                <input
                  id="affiliate-register-text"
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  maxLength={REFERRAL_REGISTER_TEXT_MAX}
                  className="w-full rounded-lg bg-dark-secondary border border-hash-gold/20 px-4 py-3 text-white mb-1 focus:outline-none focus:border-hash-gold/50"
                  placeholder="4–20 letters or numbers"
                  value={referralRegisterInput}
                  onChange={(e) => {
                    const next = e.target.value
                      .replace(/[^a-zA-Z0-9]/g, '')
                      .slice(0, REFERRAL_REGISTER_TEXT_MAX);
                    setReferralRegisterInput(next);
                  }}
                  disabled={txPending}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="text-gray-500 text-xs tabular-nums">
                    {referralRegisterInput.length}/{REFERRAL_REGISTER_TEXT_MAX}
                  </p>
                  {referralRegisterInput.length > 0 && referralRegisterInput.length < 4 ? (
                    <p className="text-amber-400/90 text-xs">At least 4 characters required.</p>
                  ) : null}
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer"
                    onClick={closeRegistration}
                    disabled={txPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-gold px-5 py-2 text-sm"
                    onClick={runReferralRegister}
                    disabled={referralRegisterDisabled}
                  >
                    {txPending ? 'Confirm in wallet…' : 'Register'}
                  </button>
                </div>
              </>
            )}
          </GlassCard>
        </div>
      )}

      <FooterSection />
    </div>
  );
}

export default Referral;
